/**
 * NHẬP TÊN MIỀN CÓ SẴN TỪ CLOUDFLARE — OQ-115 mục 3.
 *
 * Trước đó không có đường nào đưa zone đang có trên Cloudflare vào hệ thống:
 * `listZones()` là mã chết, và thông điệp lỗi 1061 chỉ người dùng sang một chức
 * năng *"Đồng bộ tên miền có sẵn"* không tồn tại. Ai đã có tên miền — gần như
 * mọi người dùng thật — phải gõ tay từng cái rồi gặp đúng lỗi ấy.
 *
 * Hai cột trạng thái được phân biệt có chủ đích: *"Đã có trong hệ thống"* thì
 * người dùng tìm ở danh sách tên miền, còn *"Người khác đang quản lý"* thì phải
 * liên hệ quản trị. Không nêu ai đang giữ — đó là oracle mà BR-1 cấm.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, thanhApiError, useLoiApi } from '../lib/api';
import { useT } from '../lib/i18n';
import { Icon } from '../components/Icon';
import { Modal } from '../components/Modal';
import { ApiErrorAlert } from '../components/ApiErrorAlert';
import { SelectField } from '../components/Field';
import { AsyncSection, EmptyState, Skeleton } from '../components/States';
import { useToast } from '../components/Toast';
import { DOMAIN_STATUS_CHIP } from '../lib/status';
import type { Connection } from '../lib/types';
import { ReimportCompareModal } from './ReimportCompareModal';
import type { ImportJob } from '../components/ImportProgress';

interface ImportableZone {
  cfZoneId: string;
  name: string;
  status: string;
  plan: string;
  accountId: string | null;
  accountName: string | null;
  alreadyImported: boolean;
  /** Đã có trong hệ thống (kết nối khác) nhưng kết nối này chưa gắn — OQ-128 việc 7. */
  existsInSystem: boolean;
}

export function ImportZonesModal({
  connections,
  onClose,
  onDone,
}: {
  connections: Connection[];
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const t = useT();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const dungDuoc = useMemo(() => connections.filter((c) => c.status === 'ok'), [connections]);
  const [chonTay, setChonTay] = useState('');
  const [chon, setChon] = useState<Set<string>>(new Set());
  const [scanRecords, setScanRecords] = useState(true);
  const { error, setError, xoaLoiKhiGo } = useLoiApi();
  const [soSanh, setSoSanh] = useState<ImportableZone | null>(null);

  /*
   * KHÔNG khởi tạo state từ `connections[0]`.
   *
   * `useState(dungDuoc[0]?.id ?? '')` chỉ chạy ở lần render đầu — lúc đó
   * `connectionsQuery` còn đang tải nên `connections` rỗng và state thành `''`.
   * Khi dữ liệu về, `<select>` hiển thị option đầu tiên (trình duyệt tự chọn khi
   * `value` không khớp option nào) nên **trông như đã chọn**, trong khi state
   * vẫn rỗng và query bị `enabled: false`. Kết quả: modal hiện ô trống với dấu
   * "—" mà không có lỗi nào.
   *
   * Tính giá trị hiệu dụng mỗi lần render thay vì đồng bộ bằng `useEffect`:
   * không có khoảnh khắc nào state và màn hình nói hai điều khác nhau.
   */
  const connectionId = chonTay || dungDuoc[0]?.id || '';

  const zonesQuery = useQuery({
    queryKey: ['importable-zones', connectionId],
    queryFn: () => api.get<ImportableZone[]>(`/domains/importable?connectionId=${connectionId}`),
    enabled: Boolean(connectionId),
  });

  const zones = zonesQuery.data ?? [];
  const nhapDuoc = zones.filter((z) => !z.alreadyImported);

  const nhap = useMutation({
    /*
     * OQ-282 — endpoint nay gio TRA VE NGAY mot `ImportJob`, khong con doi nhap
     * xong. Voi 78 ten mien, doi la doi ca vai phut voi hop thoai khoa man hinh.
     */
    mutationFn: () =>
      api.post<ImportJob>('/domains/import', {
        connectionId,
        zoneIds: [...chon],
        scanRecords,
      }),
    onSuccess: async (job) => {
      /*
       * Dong hop thoai NGAY. Tien do chuyen sang thanh tren header — no hien
       * tren moi trang, nen nguoi dung doi sang viec khac ma van theo doi duoc.
       *
       * `invalidateQueries` cho `import-job-active` de thanh tien trinh xuat
       * hien luc nay chu khong doi het chu ky 2 giay: khoang im lang giua "bam
       * Nhap" va "thay thanh" la khoang nguoi ta bam lan nua.
       */
      await queryClient.invalidateQueries({ queryKey: ['import-job-active'] });
      toast(t('domains.importQueued').replace('{n}', String(job.total)));
      onClose();
      await onDone();
    },
    onError: (err: unknown) => setError(thanhApiError(err)),
  });

  const doiChon = (id: string) => {
    setChon((truoc) => {
      const sau = new Set(truoc);
      if (sau.has(id)) sau.delete(id);
      else sau.add(id);
      return sau;
    });
  };

  const tickTatCa = () => {
    setChon((truoc) =>
      truoc.size === nhapDuoc.length ? new Set() : new Set(nhapDuoc.map((z) => z.cfZoneId)),
    );
  };

  return (
    <>
      <Modal
        open
        wide
        title={t('domains.importTitle')}
        subtitle={t('domains.importSub')}
        onClose={onClose}
        onFieldChange={xoaLoiKhiGo.onChange}
        onSubmit={() => nhap.mutate()}
        footer={
          <>
            <span className="grow small muted">
              {t('domains.importSelected').replace('{n}', String(chon.size))}
            </span>
            <button className="btn" type="button" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button
              className="btn btn-primary"
              type="submit"
              disabled={chon.size === 0 || nhap.isPending}
            >
              {nhap.isPending ? t('common.processing') : t('domains.importAction')}
            </button>
          </>
        }
      >
        <ApiErrorAlert error={error} />
        <SelectField
          label={t('domains.importConnection')}
          value={connectionId}
          onChange={(event) => {
            setChonTay(event.target.value);
            setChon(new Set());
          }}
          error={error?.fieldMessage('connectionId')}
        >
          {dungDuoc.length === 0 ? <option value="">{t('domains.importNoConn')}</option> : null}
          {dungDuoc.map((conn) => (
            <option key={conn.id} value={conn.id}>
              {conn.name}
            </option>
          ))}
        </SelectField>

        <AsyncSection
          loading={zonesQuery.isLoading || zonesQuery.isFetching}
          error={zonesQuery.error}
          data={zonesQuery.data}
          onRetry={() => void zonesQuery.refetch()}
          skeleton={<Skeleton height={180} />}
          empty={
            <EmptyState
              icon="cloud"
              title={t('domains.importEmpty')}
              body={t('domains.importEmptyHint')}
            />
          }
        >
          {() =>
            zones.length === 0 ? (
              <EmptyState
                icon="cloud"
                title={t('domains.importEmpty')}
                body={t('domains.importEmptyHint')}
              />
            ) : (
              <>
                <div className="import-head">
                  <button className="btn btn-ghost" type="button" onClick={tickTatCa}>
                    <Icon name="check" size={15} />
                    {chon.size === nhapDuoc.length && nhapDuoc.length > 0
                      ? t('domains.importNone')
                      : t('domains.importAll').replace('{n}', String(nhapDuoc.length))}
                  </button>
                </div>

                <div className="import-list">
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th
                          scope="col"
                          style={{ width: 34 }}
                          aria-label={t('domains.importPick')}
                        />
                        <th scope="col">{t('domains.importDomain')}</th>
                        <th scope="col">{t('domains.importStatus')}</th>
                        <th scope="col">{t('domains.importPlan')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {zones.map((zone) => {
                        const khoa = zone.alreadyImported;
                        const chip = DOMAIN_STATUS_CHIP[zone.status] ?? 'chip';
                        return (
                          <tr
                            key={zone.cfZoneId}
                            className={khoa ? 'import-row-locked' : undefined}
                          >
                            <td>
                              <input
                                type="checkbox"
                                checked={chon.has(zone.cfZoneId)}
                                disabled={khoa}
                                onChange={() => doiChon(zone.cfZoneId)}
                                aria-label={zone.name}
                              />
                            </td>
                            <td>
                              <b className="mono">{zone.name}</b>
                              {zone.accountName ? (
                                <div className="small muted" title={zone.accountId ?? undefined}>
                                  {zone.accountName}
                                </div>
                              ) : null}
                              {zone.alreadyImported ? (
                                <div className="small muted">{t('domains.importAlready')}</div>
                              ) : null}
                              {zone.existsInSystem ? (
                                <div className="small">
                                  <span className="muted">{t('domains.importExists')}</span>{' '}
                                  <button
                                    className="btn btn-ghost btn-sm"
                                    type="button"
                                    onClick={() => setSoSanh(zone)}
                                  >
                                    <Icon name="cloud" size={13} />
                                    {t('domains.importCompare')}
                                  </button>
                                </div>
                              ) : null}
                            </td>
                            <td>
                              <span className={chip}>{t(`domains.status.${zone.status}`)}</span>
                            </td>
                            <td className="muted">{zone.plan}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="setting-row" style={{ paddingTop: 4, borderBottom: 0 }}>
                  <div className="grow">
                    <b>{t('domains.importScan')}</b>
                    <div className="sub">{t('domains.importScanSub')}</div>
                  </div>
                  <label className="switch" aria-label={t('domains.importScan')}>
                    <input
                      type="checkbox"
                      checked={scanRecords}
                      onChange={(event) => setScanRecords(event.target.checked)}
                    />
                    <span className="track" />
                  </label>
                </div>
              </>
            )
          }
        </AsyncSection>
      </Modal>
      {soSanh ? (
        <ReimportCompareModal
          connectionId={connectionId}
          zoneId={soSanh.cfZoneId}
          zoneName={soSanh.name}
          onClose={() => setSoSanh(null)}
          onDone={async () => {
            await onDone();
            await zonesQuery.refetch();
          }}
        />
      ) : null}
    </>
  );
}
